import { formatInTimeZone } from 'date-fns-tz';
import { formatCalendarDate } from '../../../../../platform/util/calendarSystem';

export function getShareAvailabilityOptions(bookingType, staffs = []) {
  const assignments = bookingType?.assignedStaffs || [];
  const groups = assignments.map(assignment => staffs.find(staff => Number(staff.id) === Number(assignment.staffId))?.workingHourGroups?.find(group => Number(group.id) === Number(assignment.groupId)));
  return {
    timezone: bookingType?.scheduleTimezone || groups.find(Boolean)?.timeZone?.timezone || 'UTC',
    hasSchedule: groups.length > 0 && groups.every(group => Array.isArray(group?.workingHours)),
    enabledHours: groups.flatMap(group => (group?.workingHours || []).filter(hour => !hour.parentId && (hour.onJob === true || Number(hour.onJob) === 1))),
  };
}

export function getBookingShareStatus(bookingType, { timezone = 'UTC', hasSchedule = false, enabledHours, displayCalendar, now = new Date() } = {}) {
  timezone = bookingType?.scheduleTimezone || timezone;
  const day = value => {
    if (!value) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : formatInTimeZone(parsed, timezone, 'yyyy-MM-dd');
  };
  const today = day(now), start = day(bookingType?.start), end = day(bookingType?.end);
  if ((bookingType?.start && !start) || (bookingType?.end && !end) || (start && end && end < start)) {
    return { severity: 'warning', title: 'Check your booking dates', message: 'Set a valid start and end date before sharing.', action: 'Update dates', target: 'dates' };
  }
  if (end && end < today) {
    return { severity: 'warning', title: 'This booking schedule has ended', message: `Your date range ended on ${formatCalendarDate(end, displayCalendar || bookingType?.calendarSystem || 'gregory')}. Invitees won’t see any available times. Update the date range before sharing.`, action: 'Update dates', target: 'dates' };
  }
  if (hasSchedule && enabledHours?.length === 0) {
    return { severity: 'warning', title: 'No working hours are enabled', message: 'Enable availability before sharing so invitees can choose a time.', action: 'Set availability', target: 'hours' };
  }
  if (start && start > today) {
    return { severity: 'info', title: `Bookings begin on ${formatCalendarDate(start, displayCalendar || bookingType?.calendarSystem || 'gregory')}`, message: 'You can share this link now. Invitees can book available times from that date.', action: 'Update dates', target: 'dates' };
  }
  return null;
}

export function hasBookableShareSlot(days, bookings, bookingType, now = new Date(), deletedStatus) {
  const duration = Math.max(1, Number(bookingType.length || 30)) * 60000;
  const active = (bookings || []).filter(booking => Number(booking.status) !== Number(deletedStatus));
  return (days || []).some(day => (day.timeSlots || []).some(slot => {
    const start = new Date(slot.time).getTime();
    if (!Number.isFinite(start) || start <= now.getTime()) return false;
    const overlapping = active.filter(booking => {
      const otherStart = new Date(booking.time).getTime();
      const otherEnd = otherStart + Math.max(1, Number(booking.duration || bookingType.length || 30)) * 60000;
      return start < otherEnd && start + duration > otherStart;
    });
    return (slot.availableStaff || []).some(staffId => !overlapping.some(booking => !booking.staffId || Number(booking.staffId) === Number(staffId)));
  }));
}
